import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FiltersAPI from '@/api/FiltersApi';
import { IFilterMarketplaceMapping, IMarketplaceAttribute } from '@/models/filters.model';
import { StyledRzBadge } from '../CategoryItem/CategoryItemStyles';
import { showErrorToast, showSuccessToast } from '@/components';
import styled from 'styled-components';
import { COLORS, pickThemeFontStyles } from '@/theme';
import { AxiosError } from 'axios';

type Props = {
  filterId: string;
  mappings: IFilterMarketplaceMapping[];
  onMappingsChange: (next: IFilterMarketplaceMapping[]) => void;
};

type MappingGroup = {
  name: string;
  externalIds: string[];
  matchedBy: IFilterMarketplaceMapping['matched_by'];
};

const MARKETPLACE = 'rozetka';

const StyledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid ${COLORS.GREY};
  background-color: ${COLORS.GRAY_LIGHT};
  overflow: visible;
  position: relative;
  z-index: 1;
`;

const StyledLabel = styled.div`
  ${pickThemeFontStyles('14', '20', 'SEMI_BOLD')};
  color: ${COLORS.BLACK};
`;

const StyledEmpty = styled.div`
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
`;

const StyledMappingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const StyledMappingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.25rem;
  background-color: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GREY};

  & > div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
`;

const StyledPath = styled.span`
  ${pickThemeFontStyles('12', '16', 'NORMAL')};
  color: ${COLORS.DARK_GRAY};
`;

const StyledMatchedBy = styled.span`
  ${pickThemeFontStyles('10', '14', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
`;

const StyledRemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  color: ${COLORS.RED};
  padding: 0 0.25rem;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StyledSearchWrap = styled.div`
  position: relative;
  width: 100%;
  z-index: 5;
`;

const StyledSearchInput = styled.input`
  width: 100%;
  padding: 0.375rem 0.625rem;
  border: 1px solid ${COLORS.GREY};
  border-radius: 0.25rem;
  ${pickThemeFontStyles('12', '20', 'NORMAL')};
  outline: none;

  &:focus {
    border-color: ${COLORS.YELLOW_PRIMARY};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const StyledHint = styled.div`
  ${pickThemeFontStyles('12', '16', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
  margin-top: 0.25rem;
`;

const StyledSuggestionList = styled.ul`
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  z-index: 20;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid ${COLORS.GREY};
  border-radius: 0.25rem;
  background: ${COLORS.WHITE};
  max-height: 14rem;
  overflow-y: auto;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.12);
`;

const StyledSuggestionItem = styled.li<{ $isAlready: boolean }>`
  padding: 0.375rem 0.75rem;
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  cursor: ${({ $isAlready }) => ($isAlready ? 'default' : 'pointer')};
  opacity: ${({ $isAlready }) => ($isAlready ? 0.5 : 1)};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;

  &:not(:last-child) {
    border-bottom: 1px solid ${COLORS.GREY};
  }

  &:hover {
    background-color: ${({ $isAlready }) => ($isAlready ? 'transparent' : COLORS.GRAY_LIGHT)};
  }
`;

const StyledExtId = styled.span`
  ${pickThemeFontStyles('10', '14', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
`;

const resolveCategoryIds = (attr: IMarketplaceAttribute): string[] => {
  if (attr.category_external_ids && attr.category_external_ids.length > 0) {
    return attr.category_external_ids.map(String);
  }

  return attr.category_external_id ? [String(attr.category_external_id)] : [];
};

const resolveExternalIds = (attr: IMarketplaceAttribute): string[] => {
  if (Array.isArray(attr.external_ids) && attr.external_ids.length > 0) {
    const ids = attr.external_ids.map(String);
    return ids.filter((id, index) => ids.indexOf(id) === index);
  }

  return attr.external_id ? [String(attr.external_id)] : [];
};

const groupMappingsByName = (mappings: IFilterMarketplaceMapping[]): MappingGroup[] => {
  const byName = new Map<string, MappingGroup>();

  for (const mapping of mappings) {
    const name = (mapping.name || mapping.external_id).trim() || mapping.external_id;
    const existing = byName.get(name);

    if (existing) {
      if (!existing.externalIds.includes(mapping.external_id)) {
        existing.externalIds.push(mapping.external_id);
      }
      continue;
    }

    byName.set(name, {
      name,
      externalIds: [mapping.external_id],
      matchedBy: mapping.matched_by,
    });
  }

  const groups: MappingGroup[] = [];
  byName.forEach((group) => {
    groups.push(group);
  });

  return groups.sort((a, b) => a.name.localeCompare(b.name, 'uk'));
};

const FilterMarketplaceMappingsBlock = ({ filterId, mappings, onMappingsChange }: Props) => {
  const { t } = useTranslation(['categories']);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<IMarketplaceAttribute[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const mappingGroups = useMemo(() => groupMappingsByName(mappings), [mappings]);
  const mappedExternalIds = useMemo(() => new Set(mappings.map((mapping) => mapping.external_id)), [mappings]);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (!q.trim()) {
      setSuggestions([]);

      return;
    }

    setIsSearching(true);

    try {
      const res = await FiltersAPI.searchMarketplaceAttributes({
        marketplace: MARKETPLACE,
        q,
        limit: 20,
      });
      setSuggestions(res.data.data ?? []);
    } catch {
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchSuggestions(query), 300);

    return () => clearTimeout(timer);
  }, [query, fetchSuggestions]);

  const saveMapping = useCallback(
    async (next: IFilterMarketplaceMapping[]) => {
      const previous = mappings;
      setIsSaving(true);
      onMappingsChange(next);
      setSuggestions([]);
      setQuery('');

      try {
        const res = await FiltersAPI.updateMarketplaceMappings(filterId, {
          marketplace: MARKETPLACE,
          external_ids: next.map((m) => m.external_id),
        });
        const saved = res.data?.data;

        if (Array.isArray(saved)) {
          onMappingsChange(
            saved.map((item) => {
              const fallback = next.find((m) => m.external_id === item.external_id);

              return {
                ...item,
                name: item.name || fallback?.name,
              };
            }),
          );
        }

        showSuccessToast({ message: t('filter_rozetka_mapping_saved') });
      } catch (error) {
        onMappingsChange(previous);
        const axiosError = error as AxiosError<{ message?: string }>;
        const errorMessage = axiosError.response?.data?.message || t('filter_rozetka_mapping_save_error');
        showErrorToast({ message: errorMessage });
      } finally {
        setIsSaving(false);
      }
    },
    [filterId, mappings, onMappingsChange, t],
  );

  const handleAdd = (attr: IMarketplaceAttribute) => {
    const ids = resolveExternalIds(attr).filter((id) => !mappedExternalIds.has(id));

    if (ids.length === 0) {
      return;
    }

    const additions: IFilterMarketplaceMapping[] = ids.map((external_id) => ({
      marketplace: MARKETPLACE,
      external_id,
      name: attr.name,
      category_external_id: attr.category_external_id,
      matched_by: 'manual' as const,
    }));

    saveMapping([...mappings, ...additions]);
  };

  const handleRemoveGroup = (group: MappingGroup) => {
    const removeSet = new Set(group.externalIds);
    const next = mappings.filter((mapping) => !removeSet.has(mapping.external_id));
    saveMapping(next);
  };

  return (
    <StyledBlock>
      <StyledLabel>{t('filter_rozetka_mappings')}</StyledLabel>

      {mappingGroups.length > 0 ? (
        <StyledMappingList>
          {mappingGroups.map((group) => (
            <StyledMappingRow key={group.name}>
              <div>
                <StyledPath>
                  <strong>{group.name}</strong>
                </StyledPath>
                <StyledRzBadge>{t('filter_rozetka_ids_count', { count: group.externalIds.length })}</StyledRzBadge>
                <StyledMatchedBy>{group.matchedBy}</StyledMatchedBy>
              </div>
              <StyledRemoveBtn
                onClick={() => handleRemoveGroup(group)}
                disabled={isSaving}
                title={t('filter_rozetka_remove_mapping')}
              >
                ×
              </StyledRemoveBtn>
            </StyledMappingRow>
          ))}
        </StyledMappingList>
      ) : (
        <StyledEmpty>{t('filter_rozetka_no_mappings')}</StyledEmpty>
      )}

      <StyledSearchWrap>
        <StyledSearchInput
          placeholder={t('filter_rozetka_search_placeholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={isSaving}
        />

        {isSearching && <StyledHint>{t('filter_rozetka_searching')}</StyledHint>}

        {suggestions.length > 0 && (
          <StyledSuggestionList>
            {suggestions.map((attr) => {
              const ids = resolveExternalIds(attr);
              const isAlready = ids.length > 0 && ids.every((id) => mappedExternalIds.has(id));
              const categories = resolveCategoryIds(attr);

              return (
                <StyledSuggestionItem
                  key={attr.external_id}
                  onClick={() => !isAlready && handleAdd(attr)}
                  $isAlready={isAlready}
                >
                  <strong>{attr.name}</strong>
                  <StyledExtId>#{attr.external_id}</StyledExtId>
                  {categories.length > 0 && (
                    <StyledExtId>{t('filter_rozetka_category', { id: categories.join(', ') })}</StyledExtId>
                  )}
                  {isAlready && <StyledMatchedBy>{t('filter_rozetka_already_mapped')}</StyledMatchedBy>}
                </StyledSuggestionItem>
              );
            })}
          </StyledSuggestionList>
        )}
      </StyledSearchWrap>
    </StyledBlock>
  );
};

export default FilterMarketplaceMappingsBlock;
